import axios from "axios";
import heic2any from "heic2any";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import exifr from "exifr";

/**
 * Utilities shared between ContentLibrary and ContentPlanner:
 * - file type detection
 * - HEIC->JPG, MOV->MP4 conversion
 * - GCS signed upload + register_content
 * - on-prem upload + (optional) register_content
 * - helpers for names, progress, etc.
 *
 * Notes:
 * - Keeps legacy-style helpers (requestGcsUpload, postToSignedUrl, registerContent, uploadOnPrem)
 * - Adds a single high-level entrypoint: uploadAndRegister()
 * - Exposes shared Dropzone config: DROPZONE_ACCEPT, DROPZONE_MAX_SIZE
 */

// -------------------- Dropzone constants (shared) --------------------
export const DROPZONE_ACCEPT = {
  "text/html": [".html",".htm"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/gif": [".gif"],
  "image/bmp": [".bmp"],
  "image/webp": [".webp"],
  "video/mp4": [".mp4", ".m4v"],
  "video/webm": [".webm"],
  "video/3gpp": [".3gp"],
  "audio/mpeg": [".mp3"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
  "application/octet-stream": [".mov"],
  "video/quicktime": [".mov"],
  "application/pdf": [".pdf"],
};

export const DROPZONE_MAX_SIZE = 367001600; // 350MB

// -------------------- small helpers --------------------
let _ffmpegSingleton = null;
let _lastStoredFilenamePrefix = 0;

export function getMB(bytes) {
  const mb = Math.round((Number(bytes || 0) / 1024 / 1024) * 100) / 100;
  return `${mb}MB`;
}

/** Display-only cleanup of numeric uniqueness and rs_<number>_ resize prefixes. */
export function stripNumericPrefix(filename) {
  const s = String(filename || "");
  return s.replace(/^(?:(?:rs_)?\d+_)+/i, "");
}

/**
 * Creates a numeric storage prefix which remains compatible with the existing
 * display-name cleanup. Unlike the former 1..100000 random value, this cannot
 * repeat within the current browser session, even for uploads in the same ms.
 */
export function createUniqueStoredFilename(filename) {
  const now = Date.now();
  _lastStoredFilenamePrefix = Math.max(now, _lastStoredFilenamePrefix + 1);
  return `${_lastStoredFilenamePrefix}_${String(filename || "content")}`;
}

// -------------------- type detection --------------------
export function isImage(name) {
  const n = String(name || "").trim().toLowerCase();
  return [".jpg", ".jpeg", ".gif", ".png", ".webp", ".bmp", ".heic", ".heif"].some((ext) => n.endsWith(ext));
}
export function isPDF(name) {
  const n = String(name || "").trim().toLowerCase();
  return n.endsWith(".pdf");
}
export function isHTML(name) {
  const n = String(name || "").trim().toLowerCase();
  return n.endsWith(".htm") || n.endsWith(".html");
}
export function isMovie(name) {
  const n = String(name || "").trim().toLowerCase();
  return [".mpg", ".mpeg", ".mp4", ".webm", ".mkv", ".3gp", ".avi", ".mov", ".wmv", ".m4v"].some((ext) => n.endsWith(ext));
}

export function isHeic(name, mimeType) {
  const n = String(name || "").trim().toLowerCase();
  const t = String(mimeType || "").toLowerCase();
  return n.endsWith(".heic") || n.endsWith(".heif") || t === "image/heic" || t === "image/heif";
}

export function isWebp(file) {
  if (!file) return false;
  const t = (file.type || "").toLowerCase();
  const n = (file.name || "").toLowerCase();
  return t === "image/webp" || n.endsWith(".webp");
}

export function isIphoneVideo(name, mimeType) {
  const n = String(name || "").trim().toLowerCase();
  const t = String(mimeType || "").toLowerCase();
  return n.endsWith(".mov") || t === "video/quicktime";
}

// -------------------- conversions --------------------
export async function convertHeicToJpegFile(file, quality = 0.92) {
  // Read orientation from the ORIGINAL HEIC (important)
  let orientation = 1;
  try {
    const o = await exifr.parse(file, { pick: ["Orientation"] });
    orientation = o?.Orientation ?? 1;
  } catch {
    // ignore if parsing fails; we'll just keep as-is
  }
  // Convert HEIC -> JPEG
  const out = await heic2any({ blob: file, toType: "image/jpeg", quality });
  let jpegBlob = Array.isArray(out) ? out[0] : out;
  // Apply orientation by rotating pixels (fixes upside-down/sideways)
  jpegBlob = await fixOrientation(jpegBlob, orientation, quality);
  const newName = String(file?.name || "image.heic").replace(/\.(heic|heif)$/i, ".jpg");
  return new File([jpegBlob], newName, {
    type: "image/jpeg",
    lastModified: file.lastModified || Date.now(),
  });
}

async function getFFmpeg(onStatus) {
  if (_ffmpegSingleton) return _ffmpegSingleton;
  _ffmpegSingleton = new FFmpeg();
  onStatus?.("loading video converter (first time only)...");
  await _ffmpegSingleton.load();
  return _ffmpegSingleton;
}

/**
 * Convert MOV -> MP4 in browser (CPU heavy).
 * Options:
 * - onStatus: (string)=>void
 * - maxMB: number (guardrail)
 */
export async function convertMovToMp4(file, { onStatus, maxMB = 250 } = {}) {
  const sizeMB = Number(file?.size || 0) / 1024 / 1024;
  if (sizeMB > maxMB) {
    throw new Error(`video too large to convert in browser (${Math.round(sizeMB)}MB). convert server-side or upload as-is.`);
  }

  const ffmpeg = await getFFmpeg(onStatus);

  const inName = "input.mov";
  const outName = "output.mp4";
  try { await ffmpeg.deleteFile(inName); } catch {}
  try { await ffmpeg.deleteFile(outName); } catch {}

  await ffmpeg.writeFile(inName, await fetchFile(file));

  onStatus?.("converting video to mp4... (cpu intensive)");
  await ffmpeg.exec([
    "-i", inName,
    "-c:v", "libx264",
    "-preset", "veryfast",
    "-crf", "23",
    "-c:a", "aac",
    "-b:a", "128k",
    "-movflags", "+faststart",
    outName
  ]);

  const data = await ffmpeg.readFile(outName);
  const mp4Blob = new Blob([data.buffer], { type: "video/mp4" });
  const newName = String(file?.name || "video.mov").replace(/\.mov$/i, ".mp4");
  return new File([mp4Blob], newName, { type: "video/mp4" });
}


async function fileToImageBitmap(file) {
  // createImageBitmap is fast; fallback to <img> if needed
  if (window.createImageBitmap) {
    return await createImageBitmap(file);
  }

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await new Promise((res, rej) => {
      img.onload = () => res();
      img.onerror = rej;
    });

    // Make a bitmap-like object from <img>
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function convertWebpToJpeg(file, quality = 0.92) {
  const bmpOrCanvas = await fileToImageBitmap(file);

  const w = bmpOrCanvas.width;
  const h = bmpOrCanvas.height;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d", { alpha: false });

  // If input has transparency, JPEG will fill it with white.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);

  ctx.drawImage(bmpOrCanvas, 0, 0, w, h);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality)
  );

  if (!blob) throw new Error("webp->jpeg conversion failed (toBlob returned null)");

  const newName = (file.name || "image.webp").replace(/\.webp$/i, ".jpg");

  return new File([blob], newName, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}


/**
 * If image: compute ratio. If not image: -1.0
 */
export async function computeContentRatio(file) {
  if (!isImage(file?.name)) return -1.0;

  const url = URL.createObjectURL(file);
  try {
    const ratio = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img.width / img.height);
      img.onerror = reject;
      img.src = url;
    });
    return ratio;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function defaultDesc() {
  const d = new Date();
  return `${d.toLocaleDateString()} - ${d.toLocaleTimeString()}`;
}

// -------------------- Backend calls (low-level) --------------------

/**
 * Ask backend for signed upload spec for GCS form upload.
 * Returns { url, fields }
 */
export async function requestGcsUpload({ serverBase, userID, targetDir, filename, authHeaders }) {
    const url =
        `${serverBase}/content_library?action=request_upload` +
        `&user_id=${encodeURIComponent(userID)}` +
        `&target_dir=${encodeURIComponent(targetDir)}` +
        `&filename=${encodeURIComponent(filename)}`;
    const res = await fetch(url, { headers: authHeaders, cache: "no-store" });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
}

/**
 * POST file to signed form URL.
 * - uploadSpec: { url, fields }
 * - onProgress: (pct:number)=>void
 */
export async function postToSignedUrl({ uploadSpec, file, onProgress }) {
  const formData = new FormData();
  Object.entries(uploadSpec?.fields || {}).forEach(([k, v]) => formData.append(k, v));
  formData.append("file", file);

  await axios.post(uploadSpec.url, formData, {
    headers: { "content-type": "multipart/form-data" },
    onUploadProgress: (ev) => {
      const pct = Math.min(100, Math.floor((100 * (ev?.loaded || 0)) / (file?.size || 1)));
      onProgress?.(pct);
    },
  });
}

/**
 * Register content in DB after successful upload
 * Returns JSON like { content_id, thumbnail_url, media_url, ... }
 */
export async function registerContent({
  serverBase,
  authHeaders,
  userID,
  filename,
  virtualDir = "/",
  contentRatio = -1.0,
  fileSize = 0,
  desc = null,
  thumbnailUrl = null,
}) {
  const targetDir = `/${userID}/content_library`;
  const description = desc ?? filename;

  let url = `${serverBase}/content_library?action=register_content`;
  url += `&desc=${encodeURIComponent(description)}`;
  url += `&user_id=${encodeURIComponent(userID)}`;
  url += `&target_dir=${encodeURIComponent(targetDir)}`;
  url += `&content_ratio=${encodeURIComponent(contentRatio)}`;
  url += `&file_size=${encodeURIComponent(fileSize)}`;
  url += `&virtual_dir=${encodeURIComponent(virtualDir)}`;
  url += `&filename=${encodeURIComponent(filename)}`;
  if (thumbnailUrl) url += `&thumbnail_url=${encodeURIComponent(thumbnailUrl)}`;
  url += `&_ts=${Date.now()}`;

  const res = await fetch(url, { headers: authHeaders, cache: "no-store" });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * On-prem upload.
 * If server returns content_id, you can skip registerContent().
 */
export async function uploadOnPrem({
  serverBase,
  authHeaders,
  userID,
  file,
  filename,
  virtualDir = "/",
  contentRatio = -1.0,
  desc = null,
  onProgress,
}) {
  const formData = new FormData();
  formData.append("content_file", file);
  formData.append("user_id", userID);
  formData.append("filename", filename);
  formData.append("target_dir", `/${userID}/content_library`);
  formData.append("desc", desc ?? defaultDesc());
  formData.append("content_ratio", String(contentRatio));
  formData.append("virtual_dir", virtualDir);

  const url = `${serverBase}/on_premise_upload`;

  const res = await axios.post(url, formData, {
    headers: { "content-type": "multipart/form-data", ...authHeaders },
    onUploadProgress: (ev) => {
      const pct = Math.min(100, Math.floor((100 * (ev?.loaded || 0)) / (file?.size || 1)));
      onProgress?.(pct);
    },
  });

  return res?.data || {};
}

// -------------------- High-level API (preferred) --------------------

/**
 * High-level “upload then register” helper:
 * - Optional HEIC/MOV conversion
 * - Computes contentRatio for images
 * - Handles GCS vs on-prem
 * - Returns backend register result (or normalized on-prem result)
 *
 * Params:
 * - serverBase: string
 * - authHeaders: { user_id, access_token }
 * - userID: number|string
 * - file: File
 * - virtualDir: string (e.g. "/foo/")
 * - useOnPrem: boolean
 * - enableHeicConversion / enableMovConversion: boolean
 * - onStatus: (msg:string)=>void
 * - onProgress: (pct:number)=>void
 */
export async function uploadAndRegister({
  serverBase,
  authHeaders,
  userID,
  file: originalFile,
  virtualDir = "/",
  useOnPrem = false,
  enableHeicConversion = true,
  enableMovConversion = true,
  onStatus,
  onProgress,
}) {
  if (!originalFile) throw new Error("missing file");

  let file = originalFile;

  // conversions
  if (enableHeicConversion && isHeic(file.name, file.type)) {
    onStatus?.(`converting heic to jpeg... (${getMB(file.size)})`);
    file = await convertHeicToJpegFile(file, 0.92);
    onStatus?.(`converted to jpeg: ${file.name} (${getMB(file.size)})`);
  }
  if (isWebp(file)) {
    onStatus?.(`converting webp to jpeg... (${getMB(file.size)})`);
    file = await convertWebpToJpeg(file, 0.92);
    onStatus?.(`converted to jpeg: ${file.name} (${getMB(file.size)})`);
  }

  if (enableMovConversion && isIphoneVideo(file.name, file.type)) {
    onStatus?.(`converting mov to mp4... (${getMB(file.size)})`);
    // NOTE: here we use onStatus for ffmpeg status (not % progress)
    file = await convertMovToMp4(file, { onStatus });
    onStatus?.(`converted to mp4: ${file.name} (${getMB(file.size)})`);
  }

  // ratio
  const ratio = await computeContentRatio(file);

  // on-prem path
  if (useOnPrem) {
    onStatus?.(`uploading to on-prem... (${getMB(file.size)})`);

    const up = await uploadOnPrem({
      serverBase,
      authHeaders,
      userID,
      file,
      filename: file.name,
      virtualDir,
      contentRatio: ratio,
      desc: defaultDesc(),
      onProgress,
    });

    // if on-prem already registered
    const cid = up?.content_id ?? up?.contentId ?? up?.id;
    if (cid) {
      return {
        content_id: cid,
        filename: up.filename || file.name,
        thumbnail_url: up.thumbnail_url || null,
        media_url: up.media_url || null,
        status: up.status || "uploaded",
        ...up,
      };
    }

    // otherwise register explicitly
    onStatus?.("registering content...");
    return registerContent({
      serverBase,
      authHeaders,
      userID,
      filename: up.filename || file.name,
      virtualDir,
      contentRatio: ratio,
      fileSize: file.size,
      desc: defaultDesc(),
      thumbnailUrl: up.thumbnail_url || null,
    });
  }

  // GCS path
  const storedFilename = createUniqueStoredFilename(file.name);
  const targetDir = `/${userID}/content_library`;

  onStatus?.(`requesting upload... (${getMB(file.size)})`);
  const uploadSpec = await requestGcsUpload({
    serverBase,
    userID,
    targetDir,
    filename: storedFilename,
    authHeaders,
  });

  onStatus?.("uploading file...");
  await postToSignedUrl({
    uploadSpec,
    file,
    onProgress,
  });

  onStatus?.("registering content...");
  return registerContent({
    serverBase,
    authHeaders,
    userID,
    filename: storedFilename,
    virtualDir,
    contentRatio: ratio,
    fileSize: file.size,
    desc: defaultDesc(),
  });
}


async function blobToImageBitmap(blob) {
  // prefer ImageBitmap (fast)
  if (window.createImageBitmap) return await createImageBitmap(blob);

  // fallback
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
    });

    // return a canvas as "bitmap-like"
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function applyExifOrientationToCanvas(ctx, orientation, w, h) {
  // See EXIF orientation spec (1..8). We handle the common ones.
  // 1: normal
  // 3: 180
  // 6: 90 CW
  // 8: 90 CCW
  // 2/4/5/7 include mirroring (rare but we can support them too)
  switch (orientation) {
    case 2: // mirror horizontal
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      break;
    case 3: // rotate 180
      ctx.translate(w, h);
      ctx.rotate(Math.PI);
      break;
    case 4: // mirror vertical
      ctx.translate(0, h);
      ctx.scale(1, -1);
      break;
    case 5: // mirror horizontal + rotate 90 CW
      ctx.rotate(0.5 * Math.PI);
      ctx.scale(1, -1);
      break;
    case 6: // rotate 90 CW
      ctx.translate(w, 0);
      ctx.rotate(0.5 * Math.PI);
      break;
    case 7: // mirror horizontal + rotate 90 CCW
      ctx.rotate(-0.5 * Math.PI);
      ctx.scale(1, -1);
      ctx.translate(-h, 0);
      break;
    case 8: // rotate 90 CCW
      ctx.translate(0, h);
      ctx.rotate(-0.5 * Math.PI);
      break;
    default:
      break;
  }
}

async function fixOrientation(blob, orientation, quality = 0.92) {
  if (!orientation || orientation === 1) return blob;

  const bmp = await blobToImageBitmap(blob);
  const srcW = bmp.width;
  const srcH = bmp.height;

  const rotate90 = orientation >= 5 && orientation <= 8;
  const outW = rotate90 ? srcH : srcW;
  const outH = rotate90 ? srcW : srcH;

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;

  const ctx = canvas.getContext("2d", { alpha: false });
  // Fill background (JPEG has no alpha)
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, outW, outH);

  applyExifOrientationToCanvas(ctx, orientation, outW, outH);
  ctx.drawImage(bmp, 0, 0, srcW, srcH);

  const outBlob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality)
  );
  if (!outBlob) throw new Error("orientation fix failed (toBlob returned null)");
  return outBlob;
}

/**
 * Shared helper for Default Image (default_contents) used by PlaylistChannel & ContentPlanner.
 *
 * Keeps SAME backend contract:
 *  - GET  /playlist?action=default_contents&device_id=...&channel=...
 *  - GET  /playlist?action=delete_default_contents&device_id=...&channel=...
 *  - OnPrem upload: POST /file_premise_upload (multipart)
 *  - GCS upload:   GET /gcs_upload?action=request_upload...
 *                 POST to signed URL
 *                 GET /gcs_upload?action=public_access...
 */

export function buildDefaultTargetDir(deviceId, channel) {
  return `/${deviceId}/default_content/channel${channel}`;
}

export function loadDefaultImage(ETVConstants, { deviceId, channel }, cb) {
  const url =
    ETVConstants.getServerBase() +
    `/playlist?device_id=${encodeURIComponent(deviceId)}` +
    `&channel=${encodeURIComponent(String(channel))}` +
    `&action=default_contents`;
  ETVConstants.sendCMDToServerListener(url, cb);
}

export function deleteDefaultContents(ETVConstants, { deviceId, channel }, cb) {
  const url =
    ETVConstants.getServerBase() +
    `/playlist?device_id=${encodeURIComponent(deviceId)}` +
    `&channel=${encodeURIComponent(String(channel))}` +
    `&action=delete_default_contents`;
  ETVConstants.sendCMDToServerListener(url, cb);
}

export async function uploadDefaultImageFile({
  ETVConstants,
  file,
  targetDir,
  onProgress, // optional: (loaded, total) => void
  onDone,     // optional
  onError,    // optional
}) {
  try {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");

    if (ETVConstants.ON_PREMISE_VERSION) {
      // --- On-prem upload ---
      const formData = new FormData();
      formData.append("file", file);
      formData.append("filename", file.name);
      formData.append("target_dir", targetDir);

      await axios.post(ETVConstants.getServerBase() + "/file_premise_upload", formData, {
        headers: {
          "content-type": "multipart/form-data",
          user_id: reqUserID,
          access_token: accessToken,
        },
        onUploadProgress: (evt) => {
          if (onProgress) onProgress(evt.loaded, evt.total || file.size);
        },
      });

      if (onDone) onDone();
      return;
    }

    // --- GCS upload (request signed fields) ---
    const reqUrl =
      ETVConstants.getServerBase() +
      `/gcs_upload?action=request_upload&target_dir=${encodeURIComponent(targetDir)}` +
      `&filename=${encodeURIComponent(file.name)}`;

    const res = await fetch(reqUrl, {
      headers: { user_id: reqUserID, access_token: accessToken },
    });
    const result = await res.json();

    const formData = new FormData();
    Object.entries(result.fields || {}).forEach(([k, v]) => formData.append(k, v));
    formData.append("file", file);

    // POST to signed URL
    await axios.post(result.url, formData, {
      headers: { "content-type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (onProgress) onProgress(evt.loaded, evt.total || file.size);
      },
    });

    // make public
    const pubUrl =
      ETVConstants.getServerBase() +
      `/gcs_upload?action=public_access&target_dir=${encodeURIComponent(targetDir)}` +
      `&filename=${encodeURIComponent(file.name)}`;

    // keep your same listener style
    await new Promise((resolve) => {
      ETVConstants.sendCMDToServerListener(pubUrl, () => resolve());
    });

    if (onDone) onDone();
  } catch (e) {
    console.error(e);
    if (onError) onError(e);
  }
}

/** Same parsing logic as your rxDefaultImage */
export function parseDefaultImgLink(jsonObj) {
  if (!jsonObj) return undefined;
  const list = jsonObj.default_contents;
  if (!list || list.length === 0) return undefined;
  const link = list[0]?.link;
  return link || undefined;
}
